import React from "react";
import { Bullets, CTACard, Lead, P, Section } from "../../../../blog/components";
import ctaBg from "../../../../assets/demo/screen-01.png";

export const title = "为编程设计一套 Agent 循环";
export const excerpt =
  "如果你曾经把一个跑挂的测试贴回给编程 Agent，那你就是那个循环。这是一篇关于「有意去设计这套循环」的笔记。";
export const heroAlt =
  "日落时分，一条蜿蜒的河流穿过宽阔的绿色平原，伸向地平线";
export const readingTime = "6 分钟阅读";

const BLUE = "#5ba8f5";
const FAIL = "#e0795f";
/** Handwriting face (loaded in index.html) with safe fallbacks, for the sketched figures. */
const HAND = '"Architects Daughter", "Comic Sans MS", "Segoe Print", cursive';
/** Asymmetric border-radii that read as a hand-drawn rectangle; rotate through them for variety. */
const WONKY = [
  "255px 15px 225px 15px / 15px 225px 15px 255px",
  "15px 225px 18px 255px / 255px 18px 225px 15px",
  "225px 15px 255px 18px / 18px 255px 15px 225px",
  "15px 255px 15px 225px / 225px 15px 255px 18px",
];
const wonk = (i: number) => WONKY[((i % WONKY.length) + WONKY.length) % WONKY.length];
const tilt = (i: number) => [-0.6, 0.5, -0.4, 0.7, -0.3][i % 5] ?? 0;

function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

/** A hand-drawn stage box in the loop diagram. */
function LoopBox({ i, title, note, accent }: { i: number; title: string; note: string; accent?: string }) {
  return (
    <div
      style={{
        padding: "10px 16px 12px",
        borderRadius: wonk(i),
        border: `2px solid ${accent ?? "var(--border-strong)"}`,
        background: "var(--surface-5)",
        transform: `rotate(${tilt(i) * 0.5}deg)`,
        textAlign: "center",
      }}
    >
      <div style={{ fontFamily: HAND, fontSize: 17, color: "var(--text-primary)" }}>{title}</div>
      <div style={{ fontFamily: HAND, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.4, marginTop: 1 }}>{note}</div>
    </div>
  );
}

/** A short sketched arrow pointing down, optionally rotated for a fork. */
function LoopArrow({ i = 0, angle = 0, label, labelColor }: { i?: number; angle?: number; label?: string; labelColor?: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 7, height: 38 }}>
      <svg width="18" height="30" viewBox="0 0 18 30" aria-hidden style={{ flexShrink: 0, transform: `rotate(${angle + tilt(i)}deg)` }}>
        <path d="M9 2 C 7.5 8, 10.5 13, 9 20" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" />
        <path d="M4.5 19 L9 26 L13.5 19" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label && <span style={{ fontFamily: HAND, fontSize: 13.5, color: labelColor ?? "var(--text-muted)" }}>{label}</span>}
    </div>
  );
}

/**
 * The bare loop as a one-line sketch: the pipeline of words, plus a retry arc
 * from decide back up into attempt.
 */
function LoopStripFigure() {
  const arrow = (x: number, i: number) => (
    <g transform={`translate(${x} 0) rotate(${tilt(i) * 2} 12 37)`}>
      <path d="M1 37 C 8 35.5, 16 38.5, 23 37" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M16 32 L23 37 L16 42" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
  const word = (x: number, w: string, i: number) => (
    <text x={x} y={42} fontFamily={HAND} fontSize="17" fill="var(--text-primary)" transform={`rotate(${tilt(i) * 0.4} ${x} 42)`}>
      {w}
    </text>
  );
  return (
    <figure style={{ margin: "26px 0" }}>
      <div
        role="img"
        aria-label="请求、定义目标、尝试、验证、决定，再由一条 retry 弧线从决定绕回尝试，并带上证据。"
        style={{
          padding: "10px 14px 4px",
          borderRadius: wonk(1),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <svg viewBox="0 0 500 138" style={{ display: "block", width: "100%", maxWidth: 560, height: "auto", margin: "0 auto" }} aria-hidden>
          {word(10, "请求", 0)}
          {arrow(76, 0)}
          {word(106, "定义目标", 1)}
          {arrow(206, 1)}
          {word(236, "尝试", 2)}
          {arrow(308, 2)}
          {word(338, "验证", 3)}
          {arrow(394, 3)}
          {word(424, "决定", 4)}

          <path
            d="M452 56 C 458 80, 428 93, 357 94 C 296 95, 275 88, 271 64"
            fill="none"
            stroke="var(--text-muted)"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.85"
          />
          <path d="M264 69 L271 60 L278 69" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
          <text x="360" y="122" fontFamily={HAND} fontSize="13.5" fill="var(--text-secondary)" textAnchor="middle" transform="rotate(-0.5 360 122)">
            带着证据重试
          </text>
        </svg>
      </div>
    </figure>
  );
}

/**
 * The loop, sketched vertically: goal + check → attempt → observe → verify,
 * a loop-back gutter on the right carrying the failure evidence, and the two
 * terminal states at the bottom. Same hand-drawn look as the rest of the blog.
 */
function AgentLoopFigure() {
  return (
    <figure style={{ margin: "34px 0" }}>
      <div
        role="img"
        aria-label="编程 Agent 循环。一个目标加一项检查引出一次尝试、一次观察和一次验证。检查失败就带着证据绕回一次新的尝试。通过则终结于已验证的成果；循环卡住则把控制权交回给人。"
        style={{
          padding: "26px 20px",
          borderRadius: wonk(0),
          border: "2px solid var(--text-muted)",
          background: "var(--surface-4)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) clamp(64px, 16vw, 104px)",
            columnGap: 8,
            maxWidth: 480,
            margin: "0 auto",
          }}
        >
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={0} accent={BLUE} title="目标 + 检查" note="何为「完成」，以及证明它的证据" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={0} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={1} title="尝试" note="Agent 在一个隔离的 worktree 里改动" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={1} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={2} title="观察" note="收集 diff、测试输出和运行时状态" />
          </div>
          <div style={{ gridColumn: 1 }}><LoopArrow i={2} /></div>
          <div style={{ gridColumn: 1 }}>
            <LoopBox i={3} title="验证" note="拿证据去对照检查" />
          </div>

          {/* Loop-back gutter: from Verify up into Attempt, carrying the evidence. */}
          <div aria-hidden style={{ gridColumn: 2, gridRow: "3 / 8", position: "relative", margin: "26px 0" }}>
            <div
              style={{
                position: "absolute",
                inset: "0 6px 0 6px",
                border: "2px solid var(--text-muted)",
                borderLeft: "none",
                borderRadius: "0 30px 34px 0 / 0 44px 38px 0",
                opacity: 0.8,
              }}
            />
            <svg width="14" height="16" viewBox="0 0 14 16" style={{ position: "absolute", left: -4, top: -7 }}>
              <path d="M12 2 L2 8 L12 14" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
            </svg>
            <span
              style={{
                position: "absolute",
                top: "50%",
                right: -4,
                transform: "translateY(-50%)",
                background: "var(--surface-4)",
                padding: "6px 0",
                fontFamily: HAND,
                fontSize: 13,
                lineHeight: 1.4,
                color: "var(--text-secondary)",
                textAlign: "center",
                width: "min(100%, 96px)",
              }}
            >
              还不行，带着证据再来一次
            </span>
          </div>

          <div style={{ gridColumn: 1, display: "flex", justifyContent: "space-evenly" }}>
            <LoopArrow i={3} angle={26} label="通过" labelColor={BLUE} />
            <LoopArrow i={4} angle={-26} label="卡住" labelColor={FAIL} />
          </div>
          <div style={{ gridColumn: 1, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={2} accent={BLUE} title="已验证的成果" note="交给你去审查、合并或丢弃" />
            </div>
            <div style={{ flex: "1 1 170px" }}>
              <LoopBox i={3} accent={FAIL} title="交回给人" note="被卡住、超预算，或在原地打转" />
            </div>
          </div>
        </div>
      </div>
      <figcaption
        style={{ marginTop: 12, fontFamily: HAND, fontSize: 16, color: "var(--text-muted)", textAlign: "center", lineHeight: 1.5 }}
      >
        Agent 只占据「尝试」这个框。围着它的那套循环，才决定什么算完成、该信哪份证据，以及何时停下。
      </figcaption>
    </figure>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "S. Macedo，《Stop Hand-Holding Your Coding Agent: Engineering the Loops that Replace Step-by-Step Prompting》（2026）。用于区分 Agent 内部循环与外部循环规格，以及 trigger、目标、验证、停止和记忆的结构。这是一篇较新的预印本，部分基于一份手工编码的 50 个公开循环语料库。",
    href: "https://arxiv.org/abs/2607.00038",
    label: "arxiv.org/abs/2607.00038",
  },
  {
    text: "H. Guan 等，《SWE-Cycle: Benchmarking Code Agents across the Complete Issue Resolution Cycle》（2026）。用于把环境重建、实现、验证测试生成与完整循环执行分开来看。",
    href: "https://arxiv.org/abs/2605.13139",
    label: "arxiv.org/abs/2605.13139",
  },
  {
    text: "R. Wang 等，《SWE-Review: Closing the Loop on Issue Resolution with Agentic Code Review》（2026）。用于「生成—审查—修订」架构，以及结构化审查反馈所报告的价值。这是一篇很新的预印本。",
    href: "https://arxiv.org/abs/2607.06065",
    label: "arxiv.org/abs/2607.06065",
  },
  {
    text: "S. Shrivastava，《Semantic Early-Stopping for Iterative LLM Agent Loops》（2026）。用于对固定迭代次数的批评，以及节省 38% 运行 token 的结果。该实验覆盖的是 60 道多跳问答题，而非编程任务，因此这里只当作一个停止策略的例子来引用。",
    href: "https://arxiv.org/abs/2606.27009",
    label: "arxiv.org/abs/2606.27009",
  },
  {
    text: "X. Hou 等，《When Agents Do Not Stop: Uncovering Infinite Agentic Loops in LLM Agents》（2026）。用于无限 Agent 循环的定义及其对代码仓库的扫描。",
    href: "https://arxiv.org/abs/2607.01641",
    label: "arxiv.org/abs/2607.01641",
  },
  {
    text: "0-AI-UG，《Cate Agent: job-card agent that drives coding CLIs in canvas worktrees》及 Cate Agent 工具的实现（2026）。用于 Cate 的目标、迭代、worktree、driver、verifier 和结果选择工作流。",
    href: "https://github.com/0-AI-UG/cate/pull/423",
    label: "github.com/0-AI-UG/cate/pull/423",
  },
];

export default function Post() {
  return (
    <>
      <Lead>一个 prompt 只告诉编程 Agent 该做一次什么。而几乎所有要紧的事，都发生在第一份结果返回却是错的之后。</Lead>

      <P>
        假设你让一个编程 Agent 给报表页面加上 CSV 导出。它读了代码库，改了几个文件，跑了个测试，然后报告成功。测试挂了，于是你把错误贴回去。下一个版本把页面搞崩了，于是你叫它打开浏览器看一看。再下一个版本能用了，可它顺手把报表模块重写了一半。
      </P>
      <P>
        到第三轮，你就成了那个循环。你在一次次尝试之间搬运证据，也由你来判定这活儿是不是真的干完了。
      </P>
      <P>
        我感兴趣的问题是：这里头有多少能被写得足够精确，让系统自己跑起来；又有哪些写不下来，好让活儿在该回到人手上时回来，而不是永远原地打转。
      </P>

      <Section title="Agent 之外的那个循环">
        <P>
          上一段里，<em>循环</em>这个词承担了太多含义，所以有必要把这些意思拆开。有一种是程序内部那个寻常的循环。还有一种是多数 Agent 已经自己在跑的内部循环：看环境、选动作、调工具、看结果、继续走。
        </P>
        <P>我在这里关心的是第三种循环，它位于模型之外：</P>
        <LoopStripFigure />
        <P>
          最近有一篇预印本把它叫作<em>循环规格</em>（loop specification）：一个 trigger、一个目标、一段执行、一个验证步骤、一条停止规则，以及在各轮之间携带的记忆。<Fn n={1} /> 名字是新的，但里头没有一个部件是新的。持续集成（CI）多年来做的就是它的某个版本：有事发生时就跑，检查结果，记下所见，最后落在一个有名字的状态里。Agent 唯一多出来的，是一个能在两次运行之间重写自己计划的模型。
        </P>
      </Section>

      <Section title="从停止条件开始">
        <P>
          「实现 CSV 导出」描述的是一项活动。它并没有描述这项活动什么时候算完成。
        </P>
        <Bullets
          items={[
            <><strong>目标：</strong>用户能把当前筛选后的报表导出为一个编码正确的 CSV 文件。</>,
            <><strong>检查：</strong>跑导出测试、验证构建，并检查 diff 里有没有改到报表功能之外的东西。</>,
          ]}
        />
        <P>
          这一拆分才是全部要点所在。目标是应当变为真的东西。检查则是循环真正会拿来判定它有没有做到的证据。
        </P>
        <P>
          把两者合成一个，Agent 就同时当上了自己的作者、测试者和裁判。它会乐呵呵地宣布胜利——因为代码看着还算合理，因为某条命令返回了零，或者纯粹因为它已经黔驴技穷。这些没有一样意味着功能真的能用。
        </P>
        <P>
          把验证当成一架梯子、而不是单一的一道闸门，会更有帮助。Agent 的自我报告是最便宜的底层横档，单靠它几乎一文不值。一次干净的静态检查加一次通过的构建，能告诉你更多。有针对性的测试又告诉你更多，因为它们检验的正是你真正要求的行为。而运行真实产品，会暴露出任何单元测试都逮不住的集成失败；一次独立的审查，则能揪出实现 Agent 悄悄训练自己跳过的那些东西。
        </P>
        <P>
          最近的编程 Agent 研究也在往同一个方向推。SWE-Cycle 把环境搭建、实现和测试生成拆开，然后测出：一旦 Agent 得自己跑完整个循环，性能就会陡然下滑。<Fn n={2} /> SWE-Review 在一个「生成—审查—修订」循环上外挂了一个独立的审查者，发现结构化的审查反馈能让后续的修订比一次性生成更好。<Fn n={3} />
        </P>
        <P>
          验证者不一定非得是另一个模型。当一个确定性的测试就能拍板时，就用测试。只有当检查需要判断时，模型才配有一席之地：diff 有没有留在功能范围内、UI 是不是真的符合要求、这个绕过方案是不是一个你愿意接受的取舍。
        </P>
      </Section>

      <Section title="一套编程循环的结构">
        <P>
          一次尝试改动环境，观察把发生的事记下来，验证则拿这份证据去对照目标。检查失败会走向两条路之一：要么把有用的证据交给下一次尝试，要么退出到一个有名字的终结状态。
        </P>
        <AgentLoopFigure />
        <P>
          在这张图里，Agent 只是其中一个框。整套东西的行为如何，同样取决于验证者、你在各次尝试之间携带的状态、重试策略，以及决定循环何时被允许结束的那些规则。
        </P>
      </Section>

      <Section title="重试需要新信息">
        <P>把同一个 prompt 再喂给 Agent 一遍，技术上确实算个循环。只不过是个哪儿也到不了的循环。</P>
        <P>
          每一次失败的尝试，都应当给下一次交出点具体的东西：一个挂掉的断言、一张截图、一条编译器错误、一条审查者评论，甚至只是一句平白说清目标为何没达成的话。下一轮应当从这里出发，而不是从原始请求的一份全新拷贝出发。
        </P>
        <P>
          隔离每次尝试，帮上的忙比我预想的多。一条全新的 branch 或 worktree 给每次尝试一个干净的起点，让它们彼此可比，也防止一个被弃用的路子渗进紧随其后的那一次。同时跑好几次尝试，每一次本来也各需要自己的工作区。这样一来，循环就能留下真正通过验证的那份结果，而不必去把半兼容的 diff 硬拼到一起。
        </P>
        <P>
          记忆值得挑剔。循环需要的是目标、之前的裁决、那些要紧的决定，以及代码库的当前状态。它几乎从不需要每一次工具调用的完整记录。把一切都留着，只会让下一轮变得更长，而不是更聪明。
        </P>
      </Section>

      <Section title="停止是正确性的一部分">
        <P>每个循环都需要明确的终结状态。至少要有：</P>
        <Bullets
          items={[
            <><strong>success：</strong>检查通过；</>,
            <><strong>blocked：</strong>要往前走需要人，或缺少外部信息；</>,
            <><strong>exhausted：</strong>触到了尝试次数或成本上限；</>,
            <><strong>cancelled：</strong>用户中止了这项工作；</>,
            <><strong>error：</strong>循环自身无法安全地继续。</>,
          ]}
        />
        <P>
          给尝试次数封个顶依然值得，但要把它当成一道安全边界，而不是「完成」的定义。关于迭代式语言模型系统的研究表明，固定的迭代次数会在输出早已不再变好之后，仍旧继续烧算力。在一个不涉及编程的小实验里，一条语义停止规则在测得质量大致相当的情况下，把运行 token 削减了 38%。<Fn n={4} /> 这个数字并不能直接搬到软件上，但它背后的教训能搬：要盯的是循环有没有在推进，而不只是它绕了多少圈。
        </P>
        <P>
          相反的失败，是那种从来撞不上一道真正边界的循环。2026 年 7 月的一项静态分析研究考察了 6,549 个 Agent 代码仓库，确认了分布在 47 个项目里的 68 个疑似无限 Agent 循环缺陷。<Fn n={5} /> 每一例中，循环都在不断触及模型调用、工具、重试或交接，却没有一条覆盖整条反馈路径的停止条件。
        </P>
        <P>
          对编程来说，一条可行的「无进展」规则也许会在以下情形触发：两次尝试因同样的原因挂在同一项检查上；diff 不再有任何有意义的变化；或者下一步会需要 Agent 从未被授予的权限。
        </P>
      </Section>

      <Section title="循环应当始终可被检视">
        <P>
          把更多工作交给机器，并不会缩小它内部状态的重要性。反而会把那个状态变成你几乎唯一还能盯住的东西。
        </P>
        <P>
          当一个循环在跑时，开发者应当能回答关于它的那些朴素问题。此刻的目标是什么？哪一次尝试是活的？什么变了？哪项检查正在跑？上一次尝试为何失败，这一次若通过又会怎样？
        </P>
        <P>
          这正是新的 Cate Agent 背后的想法。对一项代码任务，一个只读的编排器负责设定目标和验证检查。它可以在全新的 Git worktree 里拉起一次或多次尝试。每次尝试各自的 driver 在真实的 canvas 终端里启动编程 Agent 的 CLI，等一次尝试完成后，再由一个独立的 verifier 去检查它。从这里，编排器要么采纳一份通过的结果，要么从失败证据出发再开一轮。做完的活儿会停在一个审查状态里，等你去合并、以 pull request 形式发起，或者干脆扔掉。<Fn n={6} />
        </P>
        <P>
          循环之所以保持清晰可读，是因为每个部件都有一个你能指着说的位置。尝试住在各自的 worktree 和终端里。验证有它自己的终端。你留下的那份结果，和你否掉的那些尝试，始终看得出是分开的。canvas 把整个过程铺展成一块工作区，而不是把它压扁成一段不断滚动的聊天。
        </P>
        <P>
          所以，设计一个循环，到头来大半是结构性的工作。你给一个重复的过程一个形状：一件具体的、要去满足的东西，一份用来对照检查它的证据，一点要往前携带的状态，一些真能让它停下来的限制，以及一个人还读得懂的结局。让 Agent 跑得更久，几乎无关紧要。
        </P>
        <P>
          模型负责一个个具体的步骤。而这些步骤加起来能不能变成你真正敢发布的软件，是你围着它搭起来的那个循环的属性——这一部分，仍然是你的活儿。
        </P>
      </Section>

      <CTACard download github bgImage={ctaBg} />

      <Section title="参考文献">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: BLUE, wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
